import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type ToastType = "success" | "info" | "encourage" | "error";

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook to access toast functionality
 *
 * @example
 * const { addToast } = useToast();
 *
 * // Show success message
 * addToast({ type: 'success', message: 'Great job!' });
 *
 * // Show encouraging message for mistakes
 * addToast({ type: 'encourage', message: 'Almost! Try again!' });
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Toast provider for managing non-intrusive notifications.
 * Wrap your app with this provider to enable toast notifications.
 *
 * @example
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="
        fixed bottom-4 left-1/2 -translate-x-1/2
        z-50
        flex flex-col-reverse items-center gap-3
        pointer-events-none
        max-w-[90vw]
      "
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
      ))}
    </div>,
    document.body
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);

  const duration = toast.duration ?? 4000;

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300);

    const removeTimer = setTimeout(() => {
      onDismiss(toast.id);
    }, duration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [toast.id, duration, onDismiss]);

  // Type-specific styling
  const typeStyles: Record<ToastType, { bg: string; icon: ReactNode; border: string }> = {
    success: {
      bg: "bg-gradient-to-r from-meadow-100 to-meadow-200",
      border: "border-meadow-400",
      icon: <SuccessIcon className="w-7 h-7 text-meadow-600" />,
    },
    info: {
      bg: "bg-gradient-to-r from-sky-100 to-sky-200",
      border: "border-sky-400",
      icon: <InfoIcon className="w-7 h-7 text-sky-600" />,
    },
    encourage: {
      bg: "bg-gradient-to-r from-sunshine-100 to-sunshine-200",
      border: "border-sunshine-500",
      icon: <EncourageIcon className="w-7 h-7 text-sunshine-600" />,
    },
    error: {
      bg: "bg-gradient-to-r from-red-100 to-red-200",
      border: "border-red-400",
      icon: <ErrorIcon className="w-7 h-7 text-red-600" />,
    },
  };

  const styles = typeStyles[toast.type];

  return (
    <div
      role="alert"
      className={`
        pointer-events-auto
        flex items-center gap-3
        px-5 py-4
        min-w-[200px] max-w-[400px]
        rounded-2xl
        ${styles.bg}
        border-[3px] ${styles.border}
        shadow-[0_8px_24px_rgba(0,0,0,0.15)]
        ${isExiting ? "animate-[toastOut_300ms_ease-in_forwards]" : "animate-[toastIn_300ms_cubic-bezier(0.34,1.56,0.64,1)]"}
      `}
    >
      {/* Icon */}
      <div className="flex-shrink-0">{styles.icon}</div>

      {/* Message */}
      <p className="font-body text-body-lg text-neutral-800 font-medium">
        {toast.message}
      </p>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onDismiss(toast.id), 300);
        }}
        className="
          flex-shrink-0
          w-8 h-8
          flex items-center justify-center
          rounded-full
          text-neutral-500
          hover:bg-neutral-200/50
          hover:text-neutral-700
          transition-colors
          focus:outline-none focus-visible:ring-2 focus-visible:ring-fairy-400
        "
        aria-label="Dismiss notification"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

// Icons
function SuccessIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M8 12l2.5 2.5L16 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 16v-4M12 8h.01"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EncourageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      {/* Sparkle/star shape */}
      <path
        d="M12 7l1.5 3 3 .5-2.25 2 .75 3L12 14l-3 1.5.75-3-2.25-2 3-.5L12 7z"
        fill="currentColor"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M15 9l-6 6M9 9l6 6"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// Add toast animations if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-toast-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes toastIn {
        0% {
          opacity: 0;
          transform: translateY(20px) scale(0.9);
        }
        100% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      @keyframes toastOut {
        0% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        100% {
          opacity: 0;
          transform: translateY(-10px) scale(0.95);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default ToastProvider;
